/**
 * Application Entry Point
 *
 * This is the main entry file for the KET Academy application.
 * It sets up the React application with all necessary providers and
 * renders the root component to the DOM.
 *
 * The application architecture follows these key principles:
 * 1. Component-based UI with React
 * 2. Firebase for backend services (auth, database, storage)
 * 3. Role-based access control for permissions
 * 4. Feature flags for conditional feature enablement
 * 5. React Router for navigation and routing
 *
 * This file establishes the provider hierarchy, which is critical for
 * proper context propagation throughout the component tree.
 */

import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import './index.scss'

import App from './App'
import reportWebVitals from './reportWebVitals'
import { AuthUserProvider } from './utils/firebase/auth'
import { RoleProvider } from './utils/permissions/RoleContext'

// Remove or comment out the unused function
// function scroller(element) {
//   if (element && typeof element === 'object') {
//     element.active = true;
//   } else {
//     console.warn('Attempted to set active on undefined element');
//   }
// }

/**
 * Create and render the root React component
 *
 * The provider hierarchy is:
 * 1. BrowserRouter - Enables routing throughout the app
 * 2. AuthUserProvider - Manages authentication state
 * 3. RoleProvider - Manages role-based permissions
 * 4. App - Main application component
 *
 * This hierarchy ensures that authentication and role information
 * is available throughout the application and that routing works properly.
 */
const root = ReactDOM.createRoot(document.getElementById('root'))
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <AuthUserProvider>
        <RoleProvider>
          <App />
        </RoleProvider>
      </AuthUserProvider>
    </BrowserRouter>
  </React.StrictMode>
)

/**
 * Web Vitals Reporting
 *
 * This measures and reports key performance metrics for the application.
 * It's useful for monitoring and improving the application's performance.
 *
 * Metrics include:
 * - First Contentful Paint (FCP)
 * - Largest Contentful Paint (LCP)
 * - First Input Delay (FID)
 * - Cumulative Layout Shift (CLS)
 *
 * To send these metrics to an analytics service, pass a function to reportWebVitals.
 * For more information, see: https://bit.ly/CRA-vitals
 */
reportWebVitals()
